import React, { useState, useEffect } from 'react';
import { CycleEntry } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || '';

type TabKey = 'analysis' | 'phaseTips' | 'remedies';

interface AICycleInsightsProps {
  cycles: CycleEntry[];
}

const AICycleInsights: React.FC<AICycleInsightsProps> = ({ cycles }) => {
  const { user } = useAuth();
  const userId = user?.sub || 'demo-user';
  const [activeSection, setActiveSection] = useState<TabKey>('analysis');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const getCache = (tab: TabKey) => sessionStorage.getItem(`ai_cycle_${tab}`) || '';
  const [responseText, setResponseText] = useState(() => getCache('analysis'));

  useEffect(() => {
    if (cycles.length > 0) {
      const cached = getCache(activeSection);
      if (cached) {
        setResponseText(cached);
        setError(false);
      } else {
        fetchInsights(activeSection);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycles.length, activeSection]);

  const buildPrompt = (tab: TabKey): string => {
    const summary = cycles.map(c => {
      const parts = [`Start: ${c.startDate}`];
      if (c.endDate) parts.push(`End: ${c.endDate}`);
      parts.push(`Flow: ${c.flowIntensity}`);
      if (c.cycleLength) parts.push(`Cycle length: ${c.cycleLength} days`);
      return parts.join(', ');
    }).join('\n');

    if (tab === 'analysis') {
      return `Analyze the following menstrual cycle history and identify patterns:\n${summary}\n\nNO greeting. Provide:\n1. Cycle Regularity: Are the cycles regular or irregular?\n2. Flow Patterns: Any trends in flow intensity?\n3. Cycle Length Analysis: Average length and any variations\n4. Health Insights: What these patterns might indicate\n\nUse bullet points. Be specific to the data provided.`;
    }
    if (tab === 'phaseTips') {
      const today = new Date();
      const lastCycle = cycles.length > 0
        ? cycles.reduce((a, b) => (a.startDate > b.startDate ? a : b))
        : null;
      const dayInCycle = lastCycle
        ? Math.floor((today.getTime() - new Date(lastCycle.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1
        : 1;

      return `Based on a menstrual cycle where the user is currently on day ${dayInCycle}, provide phase-specific wellness tips.\n\nNO greeting. Provide:\n1. Current Phase: Identify the likely phase (menstrual, follicular, ovulatory, or luteal)\n2. Nutrition Tips: Foods to eat and avoid during this phase\n3. Exercise Recommendations: Best types of movement for this phase\n4. Self-Care: Wellness practices for this phase\n5. Energy Management: What to expect and how to optimize\n\nUse bullet points. Be specific and actionable.`;
    }
    // remedies tab
    return `Provide traditional Ayurvedic remedies and practices for menstrual wellness based on the current cycle phase.\n\nNO greeting. Include:\n1. Herbal Remedies: 3-4 Ayurvedic herbs (like Ashoka, Shatavari, Lodhra, Dashamoola) with preparation methods\n2. Dietary Guidelines: Ayurvedic diet recommendations for menstrual health\n3. Yoga & Pranayama: Specific poses and breathing exercises\n4. Daily Routine (Dinacharya): Ayurvedic daily practices for cycle balance\n5. Home Remedies: Kitchen-based remedies using turmeric, ginger, cumin, etc.\n\nUse traditional Indian Ayurvedic wisdom. Use bullet points.`;
  };

  const fetchInsights = async (tab: TabKey, forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = getCache(tab);
      if (cached) {
        setResponseText(cached);
        setError(false);
        return;
      }
    }
    setLoading(true);
    setError(false);
    setResponseText('');
    try {
      const prompt = buildPrompt(tab);
      const res = await fetch(`${API_ENDPOINT}/ai-chat?userId=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: prompt }),
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      const reply = data.reply || 'No insights available.';
      sessionStorage.setItem(`ai_cycle_${tab}`, reply);
      setResponseText(reply);
    } catch {
      setError(true);
      setResponseText('');
    } finally {
      setLoading(false);
    }
  };

  const handleTabSwitch = (tab: TabKey) => {
    setActiveSection(tab);
  };

  const handleRefresh = () => {
    fetchInsights(activeSection, true);
  };

  const handleRetry = () => {
    fetchInsights(activeSection, true);
  };

  // Empty state
  if (cycles.length === 0) {
    return (
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-8">
        <div className="text-center py-8 text-gray-500">
          <span className="text-4xl block mb-2">🌸</span>
          <p>Log your first cycle to get AI-powered insights</p>
        </div>
      </div>
    );
  }

  const tabs: { key: TabKey; icon: string; label: string }[] = [
    { key: 'analysis', icon: '📊', label: 'Cycle Analysis' },
    { key: 'phaseTips', icon: '🌿', label: 'Phase Tips' },
    { key: 'remedies', icon: '🪷', label: 'Ayurvedic Remedies' },
  ];

  const tabMeta: Record<TabKey, { title: string; icon: string; gradient: string; bgGradient: string; borderColor: string; badgeColor: string }> = {
    analysis: {
      title: 'Cycle Pattern Analysis',
      icon: '📊',
      gradient: 'from-purple-500 to-indigo-500',
      bgGradient: 'from-purple-50/50 to-indigo-50/50',
      borderColor: 'border-purple-100',
      badgeColor: 'bg-purple-100 text-purple-700',
    },
    phaseTips: {
      title: 'Phase-Specific Wellness Tips',
      icon: '🌿',
      gradient: 'from-green-500 to-teal-500',
      bgGradient: 'from-green-50/50 to-teal-50/50',
      borderColor: 'border-green-100',
      badgeColor: 'bg-green-100 text-green-700',
    },
    remedies: {
      title: 'Ayurvedic Remedies',
      icon: '🪷',
      gradient: 'from-amber-500 to-orange-500',
      bgGradient: 'from-amber-50/50 to-orange-50/50',
      borderColor: 'border-amber-100',
      badgeColor: 'bg-amber-100 text-amber-700',
    },
  };

  const meta = tabMeta[activeSection];

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-8">
      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => handleTabSwitch(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 whitespace-nowrap ${
              activeSection === tab.key
                ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <span>{tab.icon}</span>{tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${meta.gradient} flex items-center justify-center`}>
              <span className="text-white">{meta.icon}</span>
            </div>
            <h3 className="text-lg font-bold text-gray-900">{meta.title}</h3>
            <span className={`px-2 py-0.5 ${meta.badgeColor} rounded-full text-xs font-medium`}>🤖 AI</span>
          </div>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="text-xs text-purple-600 hover:text-purple-800 disabled:text-gray-400"
          >
            🔄 Refresh
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-200 border-t-purple-600 mr-3"></div>
            <span className="text-sm text-gray-600 animate-pulse">Analyzing your cycle patterns...</span>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-gray-600 mb-3">Could not analyze cycle data. Please try again.</p>
            <button
              onClick={handleRetry}
              className="px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-xl text-sm font-medium hover:shadow-lg transition-all duration-300"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className={`prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap bg-gradient-to-br ${meta.bgGradient} rounded-xl p-5 border ${meta.borderColor}`}>
            {responseText.replace(/\*\*/g, '')}
          </div>
        )}
      </div>
    </div>
  );
};

export default AICycleInsights;
